/**
 * @module engine/ops/transformOps
 * @summary Group / ungroup, rotate, align, distribute, z-order.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 4144-4208, 3970-3978.
 */
import { S, emit, markDirty } from '../state';
import { pushUndo } from '../history';
import { toast } from '../../lib/toast';

export type AlignMode = 'left' | 'right' | 'cx' | 'top' | 'bottom' | 'cy';
export type DistAxis = 'h' | 'v';

/** Group 2+ selected shapes so they move together. */
export function groupSelected(): void {
  if (S.selected.length < 2) {
    toast('Select 2+ shapes to group');
    return;
  }
  pushUndo();
  const gid = 'g' + ++S.groupCounter;
  S.selected.forEach((id) => {
    const s = S.shapes.find((x) => x.id === id);
    if (s) s.groupId = gid;
  });
  toast('Grouped ' + S.selected.length + ' shapes');
  markDirty();
}

/** Remove group membership from the selected shapes. */
export function ungroupSelected(): void {
  if (!S.selected.length) return;
  pushUndo();
  S.selected.forEach((id) => {
    const s = S.shapes.find((x) => x.id === id);
    if (s) delete s.groupId;
  });
  toast('Ungrouped');
  markDirty();
}

/** Expand a shape id to all members of its group (for selection). */
export function expandGroupSelection(id: string): string[] {
  const s = S.shapes.find((x) => x.id === id);
  if (!s || !s.groupId) return [id];
  return S.shapes.filter((x) => x.groupId === s.groupId).map((x) => x.id);
}

/** Rotate the selection by `deg` (relative). */
export function rotateSelected(deg: number): void {
  if (!S.selected.length) return;
  pushUndo();
  S.selected.forEach((id) => {
    const s = S.shapes.find((x) => x.id === id);
    if (!s) return;
    s.rotation = (((s.rotation || 0) + deg) % 360 + 360) % 360;
  });
  markDirty();
  emit('selection');
  toast(`Rotated ${deg}°`);
}

/** Set an absolute rotation (0/90/180/270) on the selection. */
export function setRotation(deg: number): void {
  if (!S.selected.length) return;
  pushUndo();
  S.selected.forEach((id) => {
    const s = S.shapes.find((x) => x.id === id);
    if (s) s.rotation = deg;
  });
  markDirty();
  emit('selection');
}

/** Align 2+ selected shapes. */
export function alignSelected(mode: AlignMode): void {
  if (S.selected.length < 2) {
    toast('Select 2+ shapes to align');
    return;
  }
  pushUndo();
  const sel = S.shapes.filter((s) => S.selected.includes(s.id));
  if (mode === 'left') {
    const mn = Math.min(...sel.map((s) => s.x));
    sel.forEach((s) => (s.x = mn));
  } else if (mode === 'right') {
    const mx = Math.max(...sel.map((s) => s.x + s.w));
    sel.forEach((s) => (s.x = mx - s.w));
  } else if (mode === 'cx') {
    const c = (Math.min(...sel.map((s) => s.x)) + Math.max(...sel.map((s) => s.x + s.w))) / 2;
    sel.forEach((s) => (s.x = c - s.w / 2));
  } else if (mode === 'top') {
    const mn = Math.min(...sel.map((s) => s.y));
    sel.forEach((s) => (s.y = mn));
  } else if (mode === 'bottom') {
    const mx = Math.max(...sel.map((s) => s.y + s.h));
    sel.forEach((s) => (s.y = mx - s.h));
  } else if (mode === 'cy') {
    const c = (Math.min(...sel.map((s) => s.y)) + Math.max(...sel.map((s) => s.y + s.h))) / 2;
    sel.forEach((s) => (s.y = c - s.h / 2));
  }
  markDirty();
  emit('selection');
  toast('Aligned ' + mode);
}

/** Evenly distribute 3+ selected shapes along an axis. */
export function distributeSelected(axis: DistAxis): void {
  if (S.selected.length < 3) {
    toast('Select 3+ shapes to distribute');
    return;
  }
  pushUndo();
  const sel = S.shapes.filter((s) => S.selected.includes(s.id));
  if (axis === 'h') {
    sel.sort((a, b) => a.x - b.x);
    const first = sel[0];
    const last = sel[sel.length - 1];
    const totalW = sel.reduce((sum, s) => sum + s.w, 0);
    const span = last.x + last.w - first.x;
    const gap = (span - totalW) / (sel.length - 1);
    let cursor = first.x + first.w + gap;
    for (let i = 1; i < sel.length - 1; i++) {
      sel[i].x = cursor;
      cursor += sel[i].w + gap;
    }
  } else {
    sel.sort((a, b) => a.y - b.y);
    const first = sel[0];
    const last = sel[sel.length - 1];
    const totalH = sel.reduce((sum, s) => sum + s.h, 0);
    const span = last.y + last.h - first.y;
    const gap = (span - totalH) / (sel.length - 1);
    let cursor = first.y + first.h + gap;
    for (let i = 1; i < sel.length - 1; i++) {
      sel[i].y = cursor;
      cursor += sel[i].h + gap;
    }
  }
  markDirty();
  emit('selection');
  toast('Distributed ' + (axis === 'h' ? 'horizontally' : 'vertically'));
}

/** Bring the primary selected shape to the front (highest z). */
export function bringToFront(): void {
  const s = S.shapes.find((x) => x.id === S.selected[0]);
  if (!s) return;
  pushUndo();
  s.zIndex = Math.max(...S.shapes.map((x) => x.zIndex || 0)) + 1;
  markDirty();
}

/** Send the primary selected shape to the back (lowest z). */
export function sendToBack(): void {
  const s = S.shapes.find((x) => x.id === S.selected[0]);
  if (!s) return;
  pushUndo();
  s.zIndex = Math.min(...S.shapes.map((x) => x.zIndex || 0)) - 1;
  markDirty();
}
